<script>
export default {
	name: 'MobileSubPageSwiper',
	inheritAttrs: false,
};
</script>

<script setup>
import { onMounted } from 'vue';
import VueSwiper from '~/components/VueSwiper.vue';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { useRouter } from 'vue-router';

const props = defineProps({
	mobileSwiperTabs: { type: Array, default: () => [] },
	activeTab: { type: String, default: '' },
});

const { pageProperties } = useGlobalAEMState();
const country = pageProperties.value.rootCountry;
let language = pageProperties.value.rootLanguage;
const router = useRouter();
const listOfWidgetsConfigured = ref([]);
const swiperRef = ref(null);

const updateRoute = (tab) => {
	router.replace({ hash: '' });
	router.replace({ hash: tab.urlPath });
};

const activeIndex = computed(() => {
	return props.mobileSwiperTabs.findIndex((tab) => tab.id === props.activeTab);
});

const scrollToActiveTab = () => {
	const instance = swiperRef.value?.swiperInstance;
	if (instance && activeIndex.value > 3) {
		instance.slideTo(activeIndex.value, 0);
	}
};

watch(() => swiperRef.value?.swiperInstance, (instance) => {
	if (instance) {
		nextTick(() => scrollToActiveTab());
	}
});

watch(() => props.activeTab, () => {
	nextTick(() => scrollToActiveTab());
});

watch(() => props.mobileSwiperTabs, () => {
	nextTick(() => scrollToActiveTab());
}, { deep: true });

</script>

<template>
<div class="MobileSubPageSwiper">
	<VueSwiper
		ref="swiperRef"
		class="booking-tabs-swiper"
		variant="slides-auto-width"
		slidesPerView="auto"
		:spaceBetween="0"
		:navigation="true"
		:watchOverflow="true"
		:observer="true"
		:observeParents="true"
		:freeMode="true"
		:handleTabIndex="true"
		:isTabRole="true"
	>
		<button
			v-for="tab in props.mobileSwiperTabs"
			:key="tab.id"
			type="button"
			class="booking-tab"
			:class="{ 'booking-tab--active': props.activeTab === tab.id }"
			:aria-selected="props.activeTab === tab.id"
			@click="updateRoute(tab)"
		>
			{{ tab.label }}
		</button>
	</VueSwiper>
</div>

</template>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

$tab-bg: rgb(10 52 92 / 78%);
$tab-border: rgb(255 255 255 / 18%);
$tab-hover: rgb(255 255 255 / 10%);
$tab-shadow-inset: inset 0 1px 0 rgb(255 255 255 / 12%);
$tab-shadow-outer: 0 4px 12px rgb(0 0 0 / 18%);

.MobileSubPageSwiper {
	position: relative;
	width: calc(100% - 24px);
	max-width: 760px;
	margin: 0 auto;
	box-sizing: border-box;
}

.booking-tabs-swiper {
	width: 100%;
	min-width: 0;
	margin: 0 auto;
	padding: 4px 0px 4px 4px;
	border: 1px solid $tab-border;
	border-radius: 22px;
	background: $tab-bg;
	box-shadow:
		$tab-shadow-inset,
		$tab-shadow-outer;
	overflow: hidden;
	box-sizing: border-box;
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);

	:deep(.swiper-wrapper) {
		align-items: center;
	}

	:deep(.swiper-slide) {
		width: auto !important;
		height: auto;
		display: flex;
		align-items: center;
		overflow: visible;
	}
}

.booking-tab {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: max-content;
	height: 36px;
	margin: 0;
	padding: 0 14px;
	border: 0;
	border-radius: 18px;
	background: transparent;
	color: var(--neutral-white-base);
	font-size: 16px;
	font-weight: 600;
	line-height: 1;
	white-space: nowrap;
	cursor: pointer;
	min-width: 108px;
    width: auto;
	transition:
		background-color 0.2s ease,
		color 0.2s ease,
		box-shadow 0.2s ease;

	&:hover {
		background: $tab-hover;
	}

	&:focus-visible {
		outline: 2px solid var(--neutral-white-base);
		outline-offset: -2px;
	}

	&--active {
		background: var(--primary-blue-extralight);
		color: var(--primary-blue-base);;
		box-shadow:
			inset 0 0 0 1px rgb(255 255 255 / 70%),
			0 2px 5px rgb(0 0 0 / 20%);

		&:hover {
			background: var(--primary-blue-extralight);
		}
	}
}

/* Small mobile */
@media (max-width: 479px) {
	.MobileSubPageSwiper {
		width: 54%;
	}

	.booking-tabs-swiper {
		// padding-right: 34px;
		border-radius: 20px;
	}

	.booking-tab {
		font-size: 14px;
        border-radius: 17px;
        min-width: 90px;
        width: auto;
	}

	.booking-tabs-next {
		right: 7px;
		width: 26px;
		height: 26px;
	}
}

/* Large mobile and tablet */
@media (min-width: 480px) and (max-width: 1024px) {
	.MobileSubPageSwiper {
		width: calc(100% - 32px);
		max-width: 720px;
	}

	.booking-tab {
		height: 38px;
		padding: 0 16px;
		font-size: 16px;
	}
}

:deep(.swiper-button-next:after),
:deep(.swiper-button-prev:after) {
	font-size: 15px;
	border-color: var(--neutral-white-base);
	font-weight: 600;
}
:deep(.swiper-button-next),
:deep(.swiper-button-prev) {
	color: var(--neutral-white-base);
	--tw-bg-opacity: 1;
    background-color: rgba(10, 52, 92, 1);
    border-radius: 50%;
	position: absolute;
    width: 40px;
}

:deep(.swiper-button-next) {
	right: 0px;
}

:deep(.swiper-button-prev) {
	left: 0px;
}

</style>